import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Typography } from '@neo/primitives/typography'
import { useFromNumbers } from 'v2/hooks/use-from-numbers'
import { ConvChannels } from 'v2/types/api/conversations'
import { type EventCallback, EventCallbacks } from '../components/event-callbacks'
import { NodevarList } from '../components/nodevar-list'
import { initiateCallEventTypes } from '../config/initiate-call.config'
import { v2validations } from '../config/validation.config'
import type { OutboundScreeningNodeData, SidebarComponentProps } from '../types'
import {
  callStartBehaviorField,
  screeningContextCard,
  useVoicemailPortWiring,
  voicemailDetectionCard,
} from './screening-shared'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = OutboundScreeningNodeData['config']

const fields = (nodeVars: string[], fromNumbers: string[]): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).outbound_screening
  return [
    {
      name: 'name',
      componentType: 'input',
      label: 'Name',
      zodValidation: validations.shape.name,
      inputProps: {
        placeholder: 'Enter name',
      },
    },
    // Card grouping matches the Call Start Behavior / voicemail boxes so the
    // panel reads as consistent sections instead of one flat field list.
    {
      name: 'call_section',
      type: 'basic',
      component: ({ form }) => (
        <div className="col-span-2 rounded-md border border-st2 p-4 space-y-4">
          <Typography variant="H4/500" className="text-foreground">
            Outbound call
          </Typography>
          <FieldsRenderer<FormValues>
            form={form}
            fields={[
              {
                name: 'from',
                label: 'From',
                zodValidation: validations.shape.from,
                componentType: 'input',
                inputProps: {
                  placeholder: 'Enter from number',
                  endIcon: (
                    <NodevarList
                      nodevars={fromNumbers}
                      className="pr-2"
                      inputElementSelector='[name="from"]'
                    />
                  ),
                },
              },
              {
                name: 'to',
                label: 'To',
                zodValidation: validations.shape.to,
                componentType: 'input',
                inputProps: {
                  placeholder: 'Enter to number',
                  endIcon: (
                    <NodevarList
                      nodevars={nodeVars}
                      className="pr-2"
                      inputElementSelector='[name="to"]'
                    />
                  ),
                },
              },
              {
                name: 'is_validate_caller_id',
                label: 'Validate caller ID',
                componentType: 'switch',
                zodValidation: validations.shape.is_validate_caller_id,
                inputProps: {
                  description:
                    'When enabled, the from number is validated as a caller ID before dialing.',
                },
              },
            ]}
          />
        </div>
      ),
    },
    screeningContextCard<FormValues>({
      nodeVars,
      contextValidation: validations.shape.context,
      askPermissionValidation: validations.shape.ask_permission,
    }),
    callStartBehaviorField<FormValues>(validations.shape.initial_wait_time, {
      collapsible: true,
    }),
    voicemailDetectionCard<FormValues>(validations.shape.voicemail_detection_next_node),
    {
      name: 'call_settings_section',
      type: 'basic',
      component: ({ form }) => (
        <div className="col-span-2 rounded-md border border-st2 p-4 space-y-4">
          <Typography variant="H4/500" className="text-foreground">
            Call settings
          </Typography>
          <FieldsRenderer<FormValues>
            form={form}
            fields={[
              {
                name: 'ring_timeout',
                label: 'Disconnect after ringing for',
                zodValidation: validations.shape.ring_timeout,
                componentType: 'inputNumber',
                inputProps: {
                  min: 1,
                  max: 60,
                  type: 'number',
                },
              },
              {
                name: 'max_call_duration',
                label: 'Max call duration (minutes)',
                zodValidation: validations.shape.max_call_duration,
                componentType: 'inputNumber',
                inputProps: {
                  min: 1,
                  max: 240,
                  type: 'number',
                },
              },
            ]}
          />
        </div>
      ),
    },
    // Dial-half event callbacks — same widget and event set as initiate_call.
    {
      name: 'events',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="events">
            {(field) => (
              <EventCallbacks
                events={(field.state.value as EventCallback[]) ?? []}
                onChange={(events) => field.handleChange(events)}
                eventTypes={initiateCallEventTypes}
                nodeVars={nodeVars}
                className="mt-4"
              />
            )}
          </form.Field>
        </div>
      ),
    },
  ]
}

export const OutboundScreeningSidebarComponent = (
  props: SidebarComponentProps<OutboundScreeningNodeData>,
) => {
  const { nodeVars, selectedNode } = props
  const { defaultValues, handleFormSave } = useVoicemailPortWiring(selectedNode)
  // Same From affordance as initiate_call: account numbers first, then vars.
  const { accountNumbers } = useFromNumbers(ConvChannels.CALL)
  const fromNumbers = [...(accountNumbers?.map((number) => number.number) || []), ...nodeVars]
  const formFields = fields(nodeVars, fromNumbers)

  return (
    <GenericSidebarComponent
      defaultValues={defaultValues}
      fields={formFields}
      callback={handleFormSave}
      {...props}
    />
  )
}
